import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import { useDispatch, useSelector } from "react-redux";
import AnxietyForm from "./AnxietyForm";
import {
    createAnxietyAssessment,
    fetchAnxietySymptomsMaster
} from "../../../store/slices/anxietySlice";
import { fetchPatient } from "../../../store/slices/patientsSlice";

const AddAnxiety = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const { anxietySymptomsMaster, isLoading } = useSelector(state => state.anxiety);

    const [formData, setFormData] = useState({
        patient_id: patientId, // initialize
        case_id: caseId,    // initialize
        client_name: "",
        diagnosis: "",
        attending_physician: "",
        status: "active",
        // Anxiety symptoms - will be mapped to database field names
        numbness_or_tingling: "",
        feeling_hot: "",
        wobbliness_in_legs: "",
        unable_to_relax: "",
        fear_of_worst_happening: "",
        dizzy_or_lightheaded: "",
        heart_pounding_racing: "",
        unsteady: "",
        terrified_or_afraid: "",
        nervous: "",
        feeling_of_choking: "",
        hands_trembling: "",
        shaky_unsteady: "",
        fear_of_losing_control: "",
        difficulty_in_breathing: "",
        fear_of_dying: "",
        scared: "",
        indigestion: "",
        faint_lightheaded: "",
        face_flushed: "",
        hot_cold_sweats: ""
    });

    // Fetch anxiety symptoms master data & patient details on component mount
    useEffect(() => {
        dispatch(fetchAnxietySymptomsMaster());
        if (patientId) {
            dispatch(fetchPatient(patientId))
                .unwrap()
                .then((patient) => {
                    if (patient) {
                        setFormData((prev) => ({
                            ...prev,
                            client_name: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
                        }));
                    }
                })
                .catch((err) => console.error("Failed to fetch patient:", err));
        }
    }, [dispatch, patientId]);

    const handleChange = (selectedOption, actionMeta) => {
        const name = actionMeta.name;         // maps to symptom field
        const value = selectedOption?.value;  // this is the symptom ID from backend
        setFormData(prev => ({ ...prev, [name]: value }));
    };


    const calculateTotal = () => {
        const symptomFields = [
            'numbness_or_tingling', 'feeling_hot', 'wobbliness_in_legs', 'unable_to_relax',
            'fear_of_worst_happening', 'dizzy_or_lightheaded', 'heart_pounding_racing',
            'unsteady', 'terrified_or_afraid', 'nervous', 'feeling_of_choking',
            'hands_trembling', 'shaky_unsteady', 'fear_of_losing_control',
            'difficulty_in_breathing', 'fear_of_dying', 'scared', 'indigestion',
            'faint_lightheaded', 'face_flushed', 'hot_cold_sweats'
        ];

        let total = 0;
        symptomFields.forEach(field => {
            total += parseInt(formData[field] || "0", 10);
        });
        return total;
    };

    const getInterpretation = (total) => {
        if (total <= 21) return "Low anxiety";
        if (total <= 35) return "Moderate anxiety";
        return "Potentially concerning levels of anxiety";
    };

    const validateForm = () => {
        const errors = [];
        if (!formData.client_name) errors.push("Client name is required.");
        if (!formData.diagnosis) errors.push("Diagnosis is required.");
        if (!formData.attending_physician) errors.push("Attending physician is required.");

        // Require all symptoms to be filled
        const symptomFields = [
            'numbness_or_tingling', 'feeling_hot', 'wobbliness_in_legs', 'unable_to_relax',
            'fear_of_worst_happening', 'dizzy_or_lightheaded', 'heart_pounding_racing',
            'unsteady', 'terrified_or_afraid', 'nervous', 'feeling_of_choking',
            'hands_trembling', 'shaky_unsteady', 'fear_of_losing_control',
            'difficulty_in_breathing', 'fear_of_dying', 'scared', 'indigestion',
            'faint_lightheaded', 'face_flushed', 'hot_cold_sweats'
        ];

        let allFilled = true;
        symptomFields.forEach(field => {
            if (!formData[field] && formData[field] !== 0) allFilled = false;
        });

        if (!allFilled) errors.push("Please complete all 21 anxiety symptoms.");

        if (errors.length > 0) {
            Swal.fire({
                title: "Required Fields",
                html: errors.map(e => `• ${e}<br/>`).join(""),
                icon: "warning",
            });
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const total = calculateTotal();
        const interpretation = getInterpretation(total);

        const payload = {
            ...formData,
            total_score: calculateTotal(),
            anxiety_level: getInterpretation(calculateTotal())
        };

        try {
            const res = await dispatch(createAnxietyAssessment(payload)).unwrap();
            Swal.fire({
                title: "Success!",
                text: "Anxiety (BAI) assessment added successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });
            navigate(`/phase04/anxiety/edit/${res.id || res.data?.id || (res && res[0]?.id)}`, { state: { patientId: patientId || (res && res.patient_id), caseId: caseId || (res && res.case_id) } });
        } catch (err) {
            console.error("Error creating assessment:", err);
            Swal.fire({
                title: "Error",
                text: typeof err === 'object' ? JSON.stringify(err) : "Failed to save assessment",
                icon: "error",
            });
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <AnxietyForm
                                formData={formData}
                                handleChange={handleChange}
                                anxietySymptomsMaster={anxietySymptomsMaster}
                                isLoading={isLoading}
                            />
                            <div className="col-12 d-flex justify-content-end mt-3">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button
                                        type="submit"
                                        className="btn btn-primary sticky-form-submit"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <>
                                                <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <i className="ri-save-2-line me-1"></i> Save
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default AddAnxiety;